import { useRouter } from '../../lib/router'
import { MarketingLayout } from './MarketingLayout'
import { useIsMobile } from '../../lib/useIsMobile'

// ── SEO hook ─────────────────────────────────────────────────────────────────

// ── Shared primitives ─────────────────────────────────────────────────────────
function Section({ children, id, style }: { children: React.ReactNode; id?: string; style?: React.CSSProperties }) {
  return (
    <section id={id} style={{ padding: '80px var(--space-6)', ...style }}>
      <div style={{ maxWidth: 1120, margin: '0 auto' }}>
        {children}
      </div>
    </section>
  )
}

function Eyebrow({ children }: { children: React.ReactNode }) {
  return (
    <p className="eyebrow" style={{ marginBottom: 12 }}>{children}</p>
  )
}

function SectionHeading({ children, center }: { children: React.ReactNode; center?: boolean }) {
  return (
    <h2 style={{
      fontSize: 'clamp(28px, 4vw, 42px)', fontWeight: 700,
      color: 'var(--navy-900)', marginBottom: 16,
      textAlign: center ? 'center' : undefined,
    }}>{children}</h2>
  )
}

function BodyText({ children, center, style }: { children: React.ReactNode; center?: boolean; style?: React.CSSProperties }) {
  return (
    <p style={{
      fontSize: 18, lineHeight: 1.7, color: 'var(--fg-3)', maxWidth: 620,
      margin: center ? '0 auto' : undefined, textAlign: center ? 'center' : undefined,
      ...style,
    }}>{children}</p>
  )
}

// ── Pain point card ───────────────────────────────────────────────────────────
function PainCard({ icon, title, body }: { icon: string; title: string; body: string }) {
  return (
    <div style={{
      background: '#fff', border: '1px solid var(--border-subtle)', borderRadius: 12,
      padding: '24px 24px 28px', display: 'flex', flexDirection: 'column', gap: 12,
    }}>
      <span style={{ fontSize: 28 }}>{icon}</span>
      <h3 style={{ fontSize: 17, fontWeight: 600, color: 'var(--navy-800)', margin: 0 }}>{title}</h3>
      <p style={{ fontSize: 14, lineHeight: 1.6, color: 'var(--fg-3)', margin: 0 }}>{body}</p>
    </div>
  )
}

// ── Feature block ─────────────────────────────────────────────────────────────
function FeatureBlock({
  eyebrow, title, body, bullets, screenshot, screenshotAlt, screenshotFit = 'cover', flip,
}: {
  eyebrow: string
  title: string
  body: string
  bullets: string[]
  screenshot: string
  screenshotAlt: string
  screenshotFit?: React.CSSProperties['objectFit']
  flip?: boolean
}) {
  const isMobile = useIsMobile()
  return (
    <div style={{
      display: 'grid', gridTemplateColumns: isMobile ? '1fr' : '1fr 1fr', gap: 'var(--space-16)', alignItems: 'center',
    }}>
      <div style={{ order: isMobile ? undefined : (flip ? 2 : 1) }}>
        <Eyebrow>{eyebrow}</Eyebrow>
        <h3 style={{ fontSize: 28, fontWeight: 700, color: 'var(--navy-900)', marginBottom: 16, lineHeight: 1.25 }}>{title}</h3>
        <p style={{ fontSize: 16, lineHeight: 1.7, color: 'var(--fg-3)', marginBottom: 20 }}>{body}</p>
        <ul style={{ listStyle: 'none', padding: 0, margin: 0, display: 'flex', flexDirection: 'column', gap: 10 }}>
          {bullets.map(b => (
            <li key={b} style={{ display: 'flex', gap: 10, alignItems: 'flex-start' }}>
              <span style={{ color: 'var(--success-500)', marginTop: 2, flexShrink: 0 }}>✓</span>
              <span style={{ fontSize: 15, color: 'var(--fg-2)' }}>{b}</span>
            </li>
          ))}
        </ul>
      </div>
      <div style={{
        order: isMobile ? undefined : (flip ? 1 : 2),
        background: '#fff', borderRadius: 8,
        height: 280, padding: 8,
        border: '1px solid var(--navy-100)',
        overflow: 'hidden',
      }}>
        <img
          src={screenshot}
          alt={screenshotAlt}
          loading="lazy"
          style={{
            width: '100%',
            height: '100%',
            display: 'block',
            objectFit: screenshotFit,
            objectPosition: 'top left',
            borderRadius: 6,
            background: 'var(--bg-2)',
          }}
        />
      </div>
    </div>
  )
}

// ── Step card ─────────────────────────────────────────────────────────────────
function StepCard({ number, title, body }: { number: number; title: string; body: string }) {
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 16, alignItems: 'flex-start' }}>
      <div style={{
        width: 44, height: 44, borderRadius: '50%',
        background: 'var(--navy-700)', color: '#fff',
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        fontFamily: 'var(--font-display)', fontWeight: 700, fontSize: 18,
      }}>{number}</div>
      <h3 style={{ fontSize: 18, fontWeight: 600, color: 'var(--navy-900)', margin: 0 }}>{title}</h3>
      <p style={{ fontSize: 15, lineHeight: 1.6, color: 'var(--fg-3)', margin: 0 }}>{body}</p>
    </div>
  )
}

// ── Pricing tier teaser ───────────────────────────────────────────────────────
function PriceTier({ name, price, tagline, features, highlighted, comingSoon }: {
  name: string; price: string; tagline: string; features: string[]; highlighted?: boolean; comingSoon?: boolean
}) {
  const { navigate } = useRouter()
  return (
    <div style={{
      background: highlighted ? 'var(--navy-700)' : '#fff',
      border: `1px solid ${highlighted ? 'var(--navy-700)' : 'var(--border-subtle)'}`,
      borderRadius: 16, padding: '28px 24px 32px',
      display: 'flex', flexDirection: 'column', gap: 20,
      boxShadow: highlighted ? 'var(--shadow-xl)' : 'none',
      transform: highlighted ? 'scale(1.02)' : 'none',
      transition: 'box-shadow 200ms ease-out',
      opacity: comingSoon ? 0.65 : 1,
      position: 'relative',
    }}>
      {comingSoon && (
        <div style={{
          position: 'absolute', top: -12, left: '50%', transform: 'translateX(-50%)',
          background: 'var(--neutral-400, #9ca3af)', color: '#fff',
          fontSize: 11, fontWeight: 700, padding: '3px 14px', borderRadius: 20, whiteSpace: 'nowrap',
        }}>Coming soon</div>
      )}
      <div>
        <p style={{ fontSize: 13, fontWeight: 600, letterSpacing: '0.06em', textTransform: 'uppercase', marginBottom: 8, color: highlighted ? 'var(--orange-300)' : 'var(--fg-4)' }}>{name}</p>
        <div style={{ display: 'flex', alignItems: 'baseline', gap: 4, marginBottom: 4 }}>
          <span style={{ fontFamily: 'var(--font-display)', fontSize: 36, fontWeight: 700, color: highlighted ? '#fff' : 'var(--navy-900)' }}>R {price}</span>
          <span style={{ fontSize: 14, color: highlighted ? 'var(--navy-200)' : 'var(--fg-4)' }}>/mo ex-VAT</span>
        </div>
        <p style={{ fontSize: 14, color: highlighted ? 'var(--navy-200)' : 'var(--fg-3)', marginBottom: 0 }}>{tagline}</p>
      </div>
      <ul style={{ listStyle: 'none', padding: 0, margin: 0, display: 'flex', flexDirection: 'column', gap: 8 }}>
        {features.map(f => (
          <li key={f} style={{ display: 'flex', gap: 8, alignItems: 'flex-start' }}>
            <span style={{ color: highlighted ? 'var(--success-500)' : 'var(--success-500)', marginTop: 2, flexShrink: 0, fontSize: 14 }}>✓</span>
            <span style={{ fontSize: 14, color: highlighted ? 'var(--navy-100)' : 'var(--fg-2)' }}>{f}</span>
          </li>
        ))}
      </ul>
      <button
        disabled={comingSoon}
        onClick={() => !comingSoon && navigate('/contact')}
        style={{
          padding: '12px 20px', borderRadius: 8, fontSize: 14, fontWeight: 600,
          background: comingSoon ? 'var(--neutral-200, #e5e7eb)' : highlighted ? 'var(--accent)' : 'transparent',
          border: highlighted ? 'none' : '1px solid var(--border-strong)',
          color: comingSoon ? 'var(--fg-3)' : highlighted ? '#fff' : 'var(--fg-2)',
          cursor: comingSoon ? 'not-allowed' : 'pointer', marginTop: 'auto',
        }}
      >{comingSoon ? 'Coming soon' : 'Start free trial'}</button>
    </div>
  )
}

// ── Stat ──────────────────────────────────────────────────────────────────────
function Stat({ value, label }: { value: string; label: string }) {
  return (
    <div style={{ textAlign: 'center' }}>
      <p style={{ fontFamily: 'var(--font-display)', fontSize: 'clamp(24px, 5vw, 40px)', fontWeight: 700, color: 'var(--navy-700)', margin: 0, lineHeight: 1 }}>{value}</p>
      <p style={{ fontSize: 14, color: 'var(--fg-3)', margin: '8px 0 0' }}>{label}</p>
    </div>
  )
}

// ── Main ──────────────────────────────────────────────────────────────────────
export function HomePage() {
  const { navigate } = useRouter()


  return (
    <MarketingLayout>
      {/* ── Hero ─────────────────────────────────────────────── */}
      <section style={{
        background: 'linear-gradient(135deg, var(--navy-900) 0%, var(--navy-700) 100%)',
        padding: '96px var(--space-6) 112px',
        position: 'relative', overflow: 'hidden',
      }}>
        {/* Subtle pattern */}
        <div style={{
          position: 'absolute', inset: 0, opacity: 0.04,
          backgroundImage: 'radial-gradient(circle at 25% 25%, white 2px, transparent 0)',
          backgroundSize: '48px 48px',
        }} />

        <div style={{ maxWidth: 1120, margin: '0 auto', position: 'relative' }}>
          <div style={{ maxWidth: 720 }}>
            <div style={{
              display: 'inline-flex', alignItems: 'center', gap: 8,
              background: 'rgba(255,255,255,0.1)', borderRadius: 100,
              padding: '6px 14px', marginBottom: 24,
            }}>
              <span style={{ fontSize: 12, fontWeight: 700, letterSpacing: '0.08em', color: 'var(--orange-300)', textTransform: 'uppercase' }}>
                Workshop Management Software · South Africa
              </span>
            </div>

            <h1 style={{
              fontFamily: 'var(--font-display)', fontSize: 'clamp(36px, 5.5vw, 60px)',
              fontWeight: 700, color: '#fff', lineHeight: 1.1,
              letterSpacing: '-0.03em', marginBottom: 24,
            }}>
              Stop losing jobs to<br />
              <span style={{ color: 'var(--orange-400)' }}>WhatsApp chaos.</span>
            </h1>

            <p style={{
              fontSize: 'clamp(16px, 2.2vw, 20px)', lineHeight: 1.65,
              color: 'var(--navy-200)', marginBottom: 40, maxWidth: 560,
            }}>
              AutoAxxiz connects job cards, DVI, WhatsApp quotes, invoices, payment links and accounting sync in one workflow. Built for 2-10 bay independent workshops in South Africa. Billed in rand, designed around the way your customers already communicate.
            </p>

            <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap', marginBottom: 48 }}>
              <button
                onClick={() => navigate('/contact')}
                style={{
                  padding: '14px 28px', borderRadius: 10, fontSize: 16, fontWeight: 600,
                  background: 'var(--accent)', border: 'none', color: '#fff',
                  cursor: 'pointer', boxShadow: '0 4px 14px rgba(217,110,26,0.4)',
                }}
              >Start free 30-day trial</button>
              <button
                onClick={() => navigate('/pricing')}
                style={{
                  padding: '14px 28px', borderRadius: 10, fontSize: 16, fontWeight: 500,
                  background: 'rgba(255,255,255,0.1)', border: '1px solid rgba(255,255,255,0.2)',
                  color: '#fff', cursor: 'pointer',
                }}
              >See pricing</button>
            </div>

            {/* Trust bar */}
            <div style={{
              display: 'flex', gap: 32, flexWrap: 'wrap',
            }}>
              {[
                'No credit card required',
                'Free data migration',
                'SA-based support',
              ].map(t => (
                <div key={t} style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                  <span style={{ color: 'var(--success-500)', fontSize: 14 }}>✓</span>
                  <span style={{ fontSize: 14, color: 'var(--navy-300)' }}>{t}</span>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* ── Stats bar ────────────────────────────────────────── */}
      <section style={{ background: 'var(--navy-50)', borderBottom: '1px solid var(--border-subtle)' }}>
        <div style={{ maxWidth: 1120, margin: '0 auto', padding: '40px var(--space-6)' }}>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(160px, 1fr))', gap: 32 }}>
            <Stat value="30 days" label="Pilot to test your workshop workflow" />
            <Stat value="WhatsApp" label="Customer communication connected to jobs" />
            <Stat value="ZAR" label="Local pricing with VAT shown separately" />
            <Stat value="R899/mo" label="Starting price — in rand, not dollars" />
          </div>
        </div>
      </section>

      {/* ── Pain points ──────────────────────────────────────── */}
      <Section style={{ background: '#fff' }}>
        <div style={{ textAlign: 'center', marginBottom: 48 }}>
          <Eyebrow>The real cost of paper + WhatsApp + Excel</Eyebrow>
          <SectionHeading center>Problems AutoAxxiz solves on day one</SectionHeading>
          <BodyText center>
            Keep everyday workshop tasks connected. Provider-dependent features require setup and validation during your pilot.
          </BodyText>
        </div>
        <div style={{
          display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(260px, 1fr))', gap: 20,
        }}>
          <PainCard
            icon="⏰"
            title="Late and non-payment"
            body="EFT proof-of-payment fraud is real. AutoAxxiz sends a configured PayFast or SnapScan payment link embedded in the WhatsApp invoice and reconciles automatically — no more fake PoP screenshots."
          />
          <PainCard
            icon="📋"
            title="Quote follow-up black hole"
            body="Workshops typically don't know how much open quote value is sitting unanswered. AutoAxxiz shows every open quote on a live dashboard and auto-sends WhatsApp reminders after 24 hours."
          />
          <PainCard
            icon="📷"
            title="Customer disputes damage that was already there"
            body="Without photo evidence taken on arrival, you have no proof. AutoAxxiz DVI captures timestamped photos per inspection point the moment the car arrives — PASS, ADVISORY, or FAIL — and sends the report to the customer via WhatsApp before any work starts."
          />
          <PainCard
            icon="📱"
            title='"Is my car ready?" calls'
            body="Every unplanned inbound call breaks concentration. Automated WhatsApp status updates at each job-card milestone — Intake → Quoted → In Progress → Ready — eliminate most of them."
          />
          <PainCard
            icon="💬"
            title="Owner's personal WhatsApp overload"
            body="Your personal phone becomes the workshop's support inbox. AutoAxxiz provides a shared, multi-user WhatsApp Business inbox with routing — your phone stays yours."
          />
          <PainCard
            icon="📊"
            title="Month-end is a guessing game"
            body="You have no idea who owes what until the bookkeeper sends a panicked message on the 28th. AutoAxxiz shows every open invoice, every partial payment, and the total outstanding balance — live, at a glance."
          />
        </div>
      </Section>

      {/* ── Features ─────────────────────────────────────────── */}
      <Section id="features" style={{ background: 'var(--navy-50)' }}>
        <div style={{ textAlign: 'center', marginBottom: 64 }}>
          <Eyebrow>Features</Eyebrow>
          <SectionHeading center>Everything your workshop needs. Nothing it doesn't.</SectionHeading>
        </div>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 80 }}>
          <FeatureBlock
            eyebrow="Workshop control centre"
            title="See every car, quote and payment from one dashboard."
            body="Owners and office teams get a live view of cars in bay, outstanding quote value, revenue and current workshop work. The dashboard stays close to the daily rhythm of the shop floor."
            screenshot="/screenshots/dashboard.png"
            screenshotAlt="AutoAxxiz dashboard showing cars in the shop, outstanding quotes and revenue"
            bullets={[
              'Live cars-in-shop and work-in-progress view',
              'Outstanding quotes surfaced before they go stale',
              'Today and weekly payment totals at a glance',
              'Fast access to new job cards from the same screen',
            ]}
          />
          <FeatureBlock
            eyebrow="Digital vehicle inspections"
            title="Photo evidence your customers can't dispute."
            body="Technicians capture photos at each inspection point — brakes, engine, tyres — directly in the browser on any device. The system generates a customer-facing PDF report and feeds directly into the quote builder."
            screenshot="/screenshots/dvi-checklist.png"
            screenshotAlt="AutoAxxiz digital vehicle inspection checklist with PASS, ADVISORY and FAIL states"
            bullets={[
              'Photo capture per inspection point on any phone or tablet',
              'PASS / ADVISORY / FAIL rating per item',
              'PDF report sent to customer via WhatsApp',
              'Inspection feeds directly into the quote — no re-keying',
            ]}
            flip
          />
          <FeatureBlock
            eyebrow="Quote → Job card → Invoice workflow"
            title="One job. One screen. No re-keying."
            body="Parts, labour, markup and 15% VAT are calculated in the quote. Customer approves via WhatsApp. Job advances automatically. Invoice is generated from the approved quote — no copy-paste, no angry accountant."
            screenshot="/screenshots/quotes.png"
            screenshotAlt="AutoAxxiz quote detail with line items, VAT and invoice generation"
            bullets={[
              'SARS-compliant Tax Invoice PDF (VAT reg., sequential numbering)',
              'Parts markup engine built in',
              'Sequential invoice numbering per tenant (INV-2026-XXXXX)',
              'Zero re-keying from quote to invoice',
            ]}
          />
          <FeatureBlock
            eyebrow="Job card handover"
            title="Job cards carry the work from intake to DVI and collection."
            body="Every vehicle visit keeps the customer, registration, job status, quote and DVI action together. The team can move from job card to inspection without chasing paper or personal chats."
            screenshot="/screenshots/job-cards.png"
            screenshotAlt="AutoAxxiz job card detail with status flow, customer, quote and DVI action"
            bullets={[
              'Status flow from INTAKE through READY and COLLECTED',
              'Customer, vehicle and mechanic context in one view',
              'DVI action attached directly to the job card',
              'Work order and handover actions available from the same screen',
            ]}
            flip
          />
          <FeatureBlock
            eyebrow="Inspection reports"
            title="Completed DVI reports turn inspection work into customer trust."
            body="When the inspection is complete, AutoAxxiz summarises the result and separates PASS, ADVISORY and FAIL outcomes so the office can explain the work clearly."
            screenshot="/screenshots/dvi-summary.png"
            screenshotAlt="AutoAxxiz completed digital vehicle inspection summary with PASS and FAIL totals"
            bullets={[
              'Completed inspection summary for customer-ready reporting',
              'Clear PASS, ADVISORY and FAIL totals',
              'PDF download and email handover actions',
              'Evidence stays linked to the original job card',
            ]}
          />
        </div>
      </Section>

      {/* ── How it works ─────────────────────────────────────── */}
      <Section id="how-it-works" style={{ background: '#fff' }}>
        <div style={{ textAlign: 'center', marginBottom: 56 }}>
          <Eyebrow>How it works</Eyebrow>
          <SectionHeading center>A complete job in three steps</SectionHeading>
          <BodyText center>From drop-off to payment, every step is connected. No switching between apps.</BodyText>
        </div>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))', gap: 40 }}>
          <StepCard
            number={1}
            title="Car drops off — job card created"
            body="Office manager captures the vehicle reg, customer, and complaint. Job card is created with status INTAKE. Customer receives an automatic WhatsApp: 'Your Toyota Hilux GP 123-456 has been booked in at [Workshop Name]. Job #WS-2026-00142. We'll keep you updated.'"
          />
          <StepCard
            number={2}
            title="Mechanic inspects — quote sent via WhatsApp"
            body="Mechanic opens the job on their Android phone, captures inspection photos, marks items PASS/ADVISORY/FAIL. Foreman reviews, adds parts and labour. System generates a SARS-compliant PDF quote with 15% VAT. Office clicks 'Send quote via WhatsApp' — it lands on the customer's phone with a quick-reply approval button."
          />
          <StepCard
            number={3}
            title="Customer approves — invoice sent, job closed"
            body="Customer replies 'Yes please' or taps the Approve button. Job advances automatically and the assigned mechanic can be notified. When work is done, the invoice is generated from the approved quote and sent via WhatsApp with a payment link where configured. Provider webhooks can mark the invoice paid."
          />
        </div>
      </Section>

      {/* ── Pricing teaser ───────────────────────────────────── */}
      <Section style={{ background: 'var(--navy-50)' }}>
        <div style={{ textAlign: 'center', marginBottom: 48 }}>
          <Eyebrow>Pricing</Eyebrow>
          <SectionHeading center>Priced in rand. No exchange-rate surprises.</SectionHeading>
          <BodyText center>
            One fixed ZAR price per location. No per-technician fees. No USD pricing that jumps when the rand weakens.
          </BodyText>
          <p style={{ fontSize: 13, color: 'var(--fg-4)', marginTop: 8, textAlign: 'center' }}>
            All plans include a 30-day free pilot and free data migration from paper or Excel.
          </p>
        </div>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(240px, 1fr))', gap: 20, maxWidth: 900, margin: '0 auto' }}>
          <PriceTier
            name="Starter"
            price="899"
            tagline="1–2 bay shops · solo mechanic"
            features={[
              'Unlimited job cards',
              'Digital vehicle inspection',
              'Quotes with WhatsApp approval',
              'SARS-compliant tax invoices',
              'WhatsApp inbox (1 number)',
            ]}
          />
          <PriceTier
            name="Workshop"
            price="1,899"
            tagline="3–10 bays · team of up to 10 users"
            features={[
              'Everything in Starter',
              'Multi-user roles (owner, foreman, office)',
              'Up to 5 bays on the bay board',
              'Multiple WhatsApp numbers',
              'Priority SA-based support',
            ]}
            highlighted
          />
          <PriceTier
            name="Multi-site"
            price="3,499"
            tagline="Per location · 2+ branches"
            comingSoon
            features={[
              'Everything in Workshop',
              'Unlimited bays and users',
              'Multi-branch management',
              'Dedicated account manager',
              'Custom onboarding',
            ]}
          />
        </div>
        <div style={{ textAlign: 'center', marginTop: 32 }}>
          <button
            onClick={() => navigate('/pricing')}
            style={{
              padding: '12px 28px', borderRadius: 8, fontSize: 15, fontWeight: 600,
              background: 'var(--navy-700)', border: 'none', color: '#fff', cursor: 'pointer',
            }}
          >See full pricing and feature comparison</button>
        </div>
      </Section>

      {/* ── SA-specific trust section ─────────────────────────── */}
      <Section style={{ background: '#fff' }}>
        <div style={{ textAlign: 'center', marginBottom: 48 }}>
          <Eyebrow>Built for South Africa</Eyebrow>
          <SectionHeading center>Not translated from America.</SectionHeading>
          <BodyText center>
            Every design decision in AutoAxxiz was made with a Gauteng workshop in mind. Not a Silicon Valley startup looking for a new market.
          </BodyText>
        </div>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))', gap: 24, maxWidth: 880, margin: '0 auto' }}>
          {[
            {
              title: 'SARS-compliant Tax Invoices',
              body: 'Invoices include separate VAT totals and sequential numbering. Complete your workshop tax details and confirm invoice requirements with your accountant during onboarding.',
            },
            {
              title: 'POPIA-compliant data residency',
              body: 'The production architecture is designed for South African data residency, with tenant-scoped access, POPIA export, and object storage support for inspection media and PDFs.',
            },
            {
              title: 'Web dashboard plus mobile floor workflows',
              body: 'Owners and office teams use the web dashboard. Mechanics can use the mobile app for assigned jobs, clocking, DVI drafts, photo/video evidence and sync recovery.',
            },
            {
              title: 'SA-based support, SA business hours',
              body: 'Support is provided by a human in Johannesburg, Mon–Fri 08:00–17:00 SAST. No call-centre scripts. No ticket systems routing to another timezone. WhatsApp us and someone actually replies.',
            },
          ].map(item => (
            <div key={item.title} style={{
              background: 'var(--navy-50)', borderRadius: 12, padding: '24px 24px 28px',
              border: '1px solid var(--navy-100)',
            }}>
              <h3 style={{ fontSize: 17, fontWeight: 600, color: 'var(--navy-800)', marginBottom: 10 }}>{item.title}</h3>
              <p style={{ fontSize: 14, lineHeight: 1.65, color: 'var(--fg-3)', margin: 0 }}>{item.body}</p>
            </div>
          ))}
        </div>
      </Section>

      {/* ── Security & trust ──────────────────────────────────── */}
      <Section id="security" style={{ background: 'var(--navy-900)' }}>
        <div style={{ textAlign: 'center', marginBottom: 48 }}>
          <Eyebrow>Security &amp; data protection</Eyebrow>
          <SectionHeading center><span style={{ color: '#fff' }}>Your customer data stays yours.</span></SectionHeading>
          <BodyText center style={{ color: 'var(--navy-200)' }}>
            We take data protection seriously. Your workshop data and customer information are protected at every layer.
          </BodyText>
        </div>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(260px, 1fr))', gap: 24, maxWidth: 1040, margin: '0 auto' }}>
          {[
            {
              icon: '🔒',
              title: 'Encrypted at rest',
              body: 'Selected sensitive customer fields use AES-256-GCM encryption in the database. Access to deployment encryption keys is required to decrypt those fields.',
            },
            {
              icon: '🔐',
              title: 'Secure in transit',
              body: 'All traffic between your browser, the mobile app, and our servers travels over TLS. Your data is never transmitted unencrypted.',
            },
            {
              icon: '🛡️',
              title: 'POPIA compliance',
              body: 'Owners can export workshop records from Settings. Contact support for deletion requests and a confirmed production hosting and retention summary. We never sell your data.',
            },
            {
              icon: '🏦',
              title: 'Strict access control',
              body: 'Each workshop\'s data is completely isolated. Staff can only see what their role permits — mechanics see only their assigned jobs; owners see everything.',
            },
            {
              icon: '🔑',
              title: 'Strong authentication',
              body: 'Strong password requirements apply. Where owner verification is enabled, login codes are sent by email. Successful sign-ins also trigger an email alert.',
            },
            {
              icon: '📋',
              title: 'Audit trail',
              body: 'Key job, quote, invoice and payment actions have an audit trail. Invoice numbers are allocated sequentially per workshop and are not reused.',
            },
          ].map(item => (
            <div key={item.title} style={{
              background: 'rgba(255,255,255,0.05)',
              border: '1px solid rgba(255,255,255,0.1)',
              borderRadius: 12, padding: '24px 24px 28px',
            }}>
              <div style={{ fontSize: 28, marginBottom: 14 }}>{item.icon}</div>
              <h3 style={{ fontSize: 16, fontWeight: 600, color: '#fff', marginBottom: 10 }}>{item.title}</h3>
              <p style={{ fontSize: 14, lineHeight: 1.65, color: 'var(--navy-200)', margin: 0 }}>{item.body}</p>
            </div>
          ))}
        </div>
        <div style={{ textAlign: 'center', marginTop: 48 }}>
          <p style={{ fontSize: 13, color: 'var(--navy-400)' }}>
            Questions about our security practices? Email us at{' '}
            <a href="mailto:security@autoaxxiz.co.za" style={{ color: 'var(--navy-200)', textDecoration: 'underline' }}>
              security@autoaxxiz.co.za
            </a>
            {' '}or read our{' '}
            <a href="/privacy" style={{ color: 'var(--navy-200)', textDecoration: 'underline' }}>
              Privacy Policy
            </a>.
          </p>
        </div>
      </Section>

      {/* ── Final CTA ─────────────────────────────────────────── */}
      <section style={{
        background: 'linear-gradient(135deg, var(--navy-800) 0%, var(--navy-700) 100%)',
        padding: '80px var(--space-6)',
      }}>
        <div style={{ maxWidth: 640, margin: '0 auto', textAlign: 'center' }}>
          <h2 style={{
            fontFamily: 'var(--font-display)', fontSize: 36, fontWeight: 700,
            color: '#fff', marginBottom: 16, letterSpacing: '-0.02em',
          }}>Ready to run your workshop from one screen?</h2>
          <p style={{ fontSize: 18, color: 'var(--navy-200)', marginBottom: 36, lineHeight: 1.6 }}>
            Start your free 30-day pilot. Free data migration from paper or Excel. No credit card required.
          </p>
          <div style={{ display: 'flex', gap: 12, justifyContent: 'center', flexWrap: 'wrap' }}>
            <button
              onClick={() => navigate('/contact')}
              style={{
                padding: '15px 32px', borderRadius: 10, fontSize: 16, fontWeight: 600,
                background: 'var(--accent)', border: 'none', color: '#fff',
                cursor: 'pointer', boxShadow: '0 4px 14px rgba(217,110,26,0.4)',
              }}
            >Start free trial</button>
            <button
              onClick={() => navigate('/contact')}
              style={{
                padding: '15px 32px', borderRadius: 10, fontSize: 16, fontWeight: 500,
                background: 'rgba(255,255,255,0.1)', border: '1px solid rgba(255,255,255,0.25)',
                color: '#fff', cursor: 'pointer',
              }}
            >Book a demo</button>
          </div>
          <p style={{ fontSize: 13, color: 'var(--navy-400)', marginTop: 20 }}>
            Questions? WhatsApp us or call our Johannesburg office.
          </p>
        </div>
      </section>
    </MarketingLayout>
  )
}
